import { Injectable, OnDestroy } from '@angular/core';
import { withCommunity } from 'src/app/core/services/co-brand.service';
import { NavigationStart, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { ReloadSafetyService } from 'src/app/core/services/reload-safety.service';
import { UpdateService } from 'src/app/core/services/update.service';
import { LoggerService } from 'src/app/shared/services/logger.service';

/**
 * Passive half of the update flow: when a new deploy is pending, the next
 * page-to-page navigation is served as a real document load instead of an in-app
 * route change, so the user lands on the new bundle without noticing.
 *
 * A navigation is used as the trigger because it is a moment the user has ALREADY
 * chosen to leave the current screen — no guessing about whether they are busy.
 *
 * Renders nothing. The visible half is UpdateBannerComponent's "Refresh now" card.
 */
@Injectable({ providedIn: 'root' })
export class UpdateNavigationService implements OnDestroy {
  private pendingUpdate = false;
  /** Latches once the document is being replaced, so we never fire twice. */
  private applying = false;
  private readonly subscriptions = new Subscription();

  constructor(
    private updateService: UpdateService,
    private reloadSafety: ReloadSafetyService,
    private router: Router,
    private logger: LoggerService
  ) {}

  /** Called from an APP_INITIALIZER — nothing else injects this service. */
  start(): void {
    this.subscriptions.add(
      this.updateService.updateAvailable$.subscribe((available) => {
        if (available && !this.pendingUpdate) {
          this.pendingUpdate = true;
          this.logger.log('Update pending — will apply on the next page navigation');
        }
      })
    );

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
        .subscribe((event) => this.onNavigationStart(event))
    );
  }

  private onNavigationStart(event: NavigationStart): void {
    if (!this.pendingUpdate || this.applying) { return; }

    // Re-entering the same route is not a screen change; replacing the document
    // there would look like the page spontaneously reloading itself.
    if (this.stripFragment(event.url) === this.stripFragment(this.router.url)) { return; }

    const blocked = this.reloadSafety.navigationBlockingReason();
    if (blocked) {
      // Stays pending — the next navigation tries again, and the "Refresh now"
      // card is on screen the whole time as the manual route out.
      this.logger.log('Update deferred at navigation boundary', { reason: blocked });
      return;
    }

    this.applying = true;
    this.logger.log('Applying update at navigation boundary', { url: event.url });

    // On popstate the address bar already shows the target, so a plain reload
    // lands on the right screen and keeps the history entry the user moved to.
    // assign() there would push a duplicate entry and break Back.
    if (event.navigationTrigger === 'popstate') {
      void this.updateService.activateUpdateAndReload();
    } else {
      // event.url is a router URL, which never holds ?community_id=: put it back for the full page load
      void this.updateService.activateAndNavigate(withCommunity(event.url));
    }
  }

  private stripFragment(url: string): string {
    return url.split('#')[0];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
